'use client'

// Like, repost and follow from the connected BTC wallet — one shared engine
// for every row, card and profile that shows those controls.
//
// What the viewer has already done comes from Twetch (`/liked`, `/branched`,
// `/relationship`); what they do here lands on top of that at once, so a
// heart fills the moment the action settles rather than on the next poll.

import React from 'react'
import { create } from 'zustand'
import Wallet from '@/utils/wallet'
import { signMessageWithWallet } from './wallet-signature'
import { beginSocialFlow, socialFlowFailed, socialFlowSucceeded } from './social-telemetry'
import { isStaleSessionRefusal, recoverStaleSession } from './twetch-session-recovery'
import { twetchJson, TwetchError } from './twetch-api'
import { isLinkedWalletRefusal } from './twetch-post'
import {
	bip322Capable,
	classifyLoginError,
	loginWithWallet,
	TWETCH_ALGORITHM,
	type TwetchIdentity
} from './twetch-auth'
import { loadDeviceKey } from './twetch-crypto'
import {
	branchTarget,
	followTarget,
	likeTarget,
	sendSponsoredAction,
	type TwetchAction
} from './twetch-actions'
import { sessionDeviceKey, useTwetch } from './twetch-store'
import useTwetchIdentities from './useTwetchIdentities'
import { identityForAddress } from './identity-match'

interface ActionState {
	liked: Record<number, boolean>
	branched: Record<number, boolean>
	following: Record<number, boolean>
	/// The least a count can be after this session's own action (the count at
	/// the time + 1). A floor, not a delta: once the feed refetches a number
	/// that already includes the action, nothing is added twice.
	likeFloor: Record<number, number>
	branchFloor: Record<number, number>
	busy: string | null
	error: { key: string; message: string } | null
	/// The Twetch account these maps describe — a wallet switch clears them.
	viewer: number | null
	set: (patch: Partial<ActionState>) => void
}

const useActionState = create<ActionState>((set) => ({
	liked: {},
	branched: {},
	following: {},
	likeFloor: {},
	branchFloor: {},
	busy: null,
	error: null,
	viewer: null,
	set: (patch) => set(patch)
}))

// ── what the viewer already did ─────────────────────────────────────────────
//
// Twetch answers "which of THESE posts has this user liked / reposted"
// (`?ids=1,2,3`), never "everything they ever liked". Every mounted row asks
// for its own post; the asks are gathered for a moment and sent as ONE request
// per kind, and the answers land in the shared maps above.

const asked = new Set<number>()
let queue: number[] = []
let flushTimer: number | undefined

function flushViewerState(uid: number) {
	const ids = queue
	queue = []
	flushTimer = undefined
	if (ids.length === 0) return
	const param = ids.join(',')
	const apply = (kind: 'liked' | 'branched', hits: number[] | undefined) => {
		const state = useActionState.getState()
		if (state.viewer !== uid) return
		const hit = new Set(hits ?? [])
		// Never overwrite something the viewer did in this session.
		const next = { ...Object.fromEntries(ids.map((id) => [id, hit.has(id)])), ...state[kind] }
		state.set({ [kind]: next } as Partial<ActionState>)
	}
	void twetchJson<{ liked?: number[] }>(`/v1/users/${uid}/liked?ids=${param}`)
		.then((body) => apply('liked', body.liked))
		.catch(() => ids.forEach((id) => asked.delete(id)))
	void twetchJson<{ branched?: number[] }>(`/v1/users/${uid}/branched?ids=${param}`)
		.then((body) => apply('branched', body.branched))
		.catch(() => ids.forEach((id) => asked.delete(id)))
}

function askViewerState(uid: number, postId: number) {
	if (asked.has(postId)) return
	asked.add(postId)
	queue.push(postId)
	if (queue.length >= 150) {
		window.clearTimeout(flushTimer)
		flushViewerState(uid)
		return
	}
	flushTimer ??= window.setTimeout(() => flushViewerState(uid), 40)
}

function isWalletRejection(e: unknown): boolean {
	return /reject|declin|cancel|denied/i.test(String((e as { message?: string })?.message ?? ''))
}

function failureMessage(e: unknown): string {
	if (isWalletRejection(e)) return 'You cancelled in the wallet. Nothing was sent.'
	if (e instanceof TwetchError) {
		if (e.status === 403) return e.message || "This action isn't available for this account yet."
		if (e.status === 503) return 'Free actions are paused for a moment. Try again shortly.'
		return e.message
	}
	return (e as Error)?.message || "That didn't go through."
}

/// `postId`: the post this caller renders controls for, so its liked/reposted
/// state is looked up.
export default function useTwetchActions(postId?: number) {
	const storedIdentity = useTwetch((s) => s.identity)
	const setIdentity = useTwetch((s) => s.setIdentity)
	const address = Wallet.getTaprootAddress()
	const identity = identityForAddress(storedIdentity, address)
	// The viewer is known from the wallet alone — Twetch's public address index
	// names the account — so hearts stay lit with no Twetch session at all
	// (a fresh browser, or after "Disconnect Twetch").
	const identities = useTwetchIdentities(address ? [address] : [])
	const uid = identity?.uid ?? (address ? identities[address]?.id ?? null : null)
	const state = useActionState()

	React.useEffect(() => {
		if (useActionState.getState().viewer === uid) return
		asked.clear()
		queue = []
		useActionState.getState().set({
			viewer: uid,
			liked: {},
			branched: {},
			following: {},
			likeFloor: {},
			branchFloor: {}
		})
	}, [uid])
	React.useEffect(() => {
		if (uid !== null && postId !== undefined && state.viewer === uid) {
			askViewerState(uid, postId)
		}
	}, [uid, postId, state.viewer])

	/// An established Twetch session for the connected wallet — signing in
	/// (one popup) when the account exists but this browser has no session.
	const ensureSession = async (): Promise<{
		identity: TwetchIdentity
		deviceKey: NonNullable<ReturnType<typeof loadDeviceKey>>
	}> => {
		const deviceKey = sessionDeviceKey(identity)
		if (identity && deviceKey) {
			return { identity, deviceKey }
		}
		if (!address || !bip322Capable(address)) {
			throw new Error('Connect a mainnet bc1 wallet that can sign messages.')
		}
		const flow = beginSocialFlow('social_login', 'sign_login')
		try {
			const next = await loginWithWallet({
				address,
				signMessage: signMessageWithWallet
			})
			setIdentity(next)
			const key = loadDeviceKey()
			if (!key) throw new Error('Twetch could not open a session for this wallet.')
			socialFlowSucceeded(flow, 'sign_login')
			return { identity: next, deviceKey: key }
		} catch (e) {
			socialFlowFailed(flow, 'sign_login', e, classifyLoginError(e), { address })
			if (classifyLoginError(e) === 'no-account') {
				throw new Error(
					'Set up your profile or publish a post first — that creates your free account.'
				)
			}
			throw e
		}
	}

	const run = async (
		action: TwetchAction,
		key: string,
		target: ReturnType<typeof likeTarget>,
		onDone: () => void
	) => {
		if (useActionState.getState().busy) return
		state.set({ busy: key, error: null })
		const flow = beginSocialFlow('social_action', 'sign_action')
		let stage: 'sign_action' | 'send_action' = 'sign_action'
		try {
			// A LINKED wallet is refused a session (that would rotate the
			// account's key) but may still authorize the action itself — the
			// wallet signature the sponsored write carries is what the server
			// verifies. Its likes used to die on this refusal.
			let session: {
				identity: TwetchIdentity
				deviceKey: ReturnType<typeof loadDeviceKey> | null
			}
			try {
				session = await ensureSession()
			} catch (e) {
				if (!isLinkedWalletRefusal(e)) throw e
				if (uid === null || !address) throw e
				session = {
					identity: {
						uid,
						address,
						algorithm: TWETCH_ALGORITHM,
						// Unused by a sponsored action: the wallet signs it,
						// and there is no session key to name.
						handle: '',
						devicePubkey: ''
					},
					deviceKey: null
				}
			}
			const send = (identity: TwetchIdentity, deviceKey: ReturnType<typeof loadDeviceKey> | null) =>
				sendSponsoredAction({
					action,
					target,
					identity,
					deviceKey,
					signWithWallet: async (text) => {
						const sig = await signMessageWithWallet(text)
						stage = 'send_action'
						return sig
					}
				})
			try {
				await send(session.identity, session.deviceKey)
			} catch (e) {
				// A sign-in elsewhere rotated the account's device key: this
				// browser's session is stale. Sign in again (one popup) and
				// retry once, instead of surfacing a bare "invalid signature".
				if (!isStaleSessionRefusal(e) || !address) throw e
				socialFlowFailed(flow, stage, e, `${action}_session_stale`, { address })
				const fresh = await recoverStaleSession(address)
				setIdentity(fresh.identity)
				await send(fresh.identity, fresh.deviceKey)
			}
			socialFlowSucceeded(flow, 'send_action')
			onDone()
		} catch (e) {
			const cancelled = isWalletRejection(e)
			const status = (e as { status?: number })?.status
			const kind = cancelled
				? 'wallet_rejected'
				: e instanceof TwetchError
				? /signature|verify/i.test(e.message)
					? 'identity_mismatch'
					: status === 403
					? 'ineligible'
					: status === 503
					? 'unavailable'
					: 'rejected'
				: 'other'
			socialFlowFailed(flow, stage, e, `${action}_${kind}`, {
				address,
				cancelled
			})
			useActionState.getState().set({ error: { key, message: failureMessage(e) } })
		} finally {
			useActionState.getState().set({ busy: null })
		}
	}

	return {
		/// A wallet that can sign is connected (actions can at least be tried).
		canAct: !!address && bip322Capable(address),
		busyKey: state.busy,
		error: state.error,
		clearError: () => state.set({ error: null }),
		isLiked: (id: number) => state.liked[id] ?? false,
		isBranched: (id: number) => state.branched[id] ?? false,
		isFollowing: (userId: number, known?: boolean) => state.following[userId] ?? known ?? false,
		likeCount: (id: number, reported: number | undefined) =>
			Math.max(reported ?? 0, state.likeFloor[id] ?? 0),
		branchCount: (id: number, reported: number | undefined) =>
			Math.max(reported ?? 0, state.branchFloor[id] ?? 0),
		like: (post: { id: number; txid?: string | null; numLikes?: number }) =>
			run('like', `like-${post.id}`, likeTarget(post), () => {
				const s = useActionState.getState()
				// Twetch answers a repeat like with success and records nothing,
				// so only a like that was new raises the count.
				const fresh = !s.liked[post.id]
				s.set({
					liked: { ...s.liked, [post.id]: true },
					likeFloor: fresh ? { ...s.likeFloor, [post.id]: (post.numLikes ?? 0) + 1 } : s.likeFloor
				})
			}),
		branch: (post: { id: number; txid?: string | null; numBranches?: number }) =>
			run('branch', `branch-${post.id}`, branchTarget(post), () => {
				const s = useActionState.getState()
				const fresh = !s.branched[post.id]
				s.set({
					branched: { ...s.branched, [post.id]: true },
					branchFloor: fresh
						? { ...s.branchFloor, [post.id]: (post.numBranches ?? 0) + 1 }
						: s.branchFloor
				})
			}),
		follow: (userId: number) =>
			run('follow', `follow-${userId}`, followTarget(userId), () => {
				const s = useActionState.getState()
				s.set({ following: { ...s.following, [userId]: true } })
			}),
		viewerId: uid
	}
}
